/**
 * The channel logo in the chat header for LINE / Messenger / Instagram chats (owner 27 ก.ย. 69): neither LINE OA
 * Manager nor Meta Business Suite can be opened on one customer's chat from the ids their APIs give, so the logo opens
 * what people pasted (api/app/line_oa.py, api/app/meta_chat.py) — the customer's own chat once staff linked it, else
 * the OA's chat list / the Page's inbox with the customer's name copied for its search box in the same click. LINE OA
 * not set up yet → the owner gets a link to ตั้งค่า › ช่องทางที่เชื่อม, everyone else the logo with a tooltip.
 * Comments keep their post link (lib/platformLink.ts).
 */
import type { PlatformLinkState } from '../api/platformChat'
import type { StaffRole } from '../api/session'
import type { Channel, ConversationKind } from '../api/types'
import { SETUP_WHERE } from './lineOa'

/** Chats whose logo works from pasted links: LINE 1:1 + groups, Messenger / Instagram 1:1 (not history). */
export function usesPastedLink(channel: Channel, kind: ConversationKind | undefined, isHistory = false): boolean {
  if (isHistory) return false
  const k = kind ?? 'dm'
  if (channel === 'line') return k === 'dm' || k === 'group'
  return (channel === 'fb' || channel === 'ig') && k === 'dm'
}

/** The customer panel's "ผูกลิงก์แชท" (1:1 chats only). */
export function canPasteChat(channel: Channel, kind: ConversationKind | undefined, isHistory = false): boolean {
  return usesPastedLink(channel, kind, isHistory) && (kind ?? 'dm') === 'dm'
}

/** Where the pasted links live, in Thai. */
export function appName(channel: Channel): string {
  return channel === 'line' ? 'LINE OA' : 'Meta Business Suite'
}

export type LogoKind = 'link' | 'setup' | 'none'

export interface LogoView {
  kind: LogoKind
  /** tooltip + accessible name */
  title: string
  /** copy the customer's (group's) name for the search box when the logo opens the list / inbox */
  copyName: boolean
}

/** What the logo in the chat header does, from GET …/platform-link (`state`) and who is looking. */
export function chatLogoView(
  state: Pick<PlatformLinkState, 'url' | 'target'> | 'error' | null | undefined,
  channel: Channel,
  kind: ConversationKind | undefined,
  role: StaffRole | null | undefined,
): LogoView | null {
  const app = appName(channel)
  if (state === 'error') return { kind: 'none', title: `โหลดลิงก์ ${app} ไม่สำเร็จ ลองเปิดแชทนี้ใหม่อีกครั้ง`, copyName: false }
  if (!state) return null // still loading: just the logo
  if (!state.url) {
    return role === 'owner'
      ? { kind: 'setup', title: `ตั้งลิงก์ LINE OA ที่ ${SETUP_WHERE}`, copyName: false }
      : { kind: 'none', title: `ยังไม่ได้ตั้งลิงก์ LINE OA · เจ้าของตั้งได้ที่ ${SETUP_WHERE}`, copyName: false }
  }
  if (state.target === 'thread') return { kind: 'link', title: `เปิดแชทนี้ใน ${app}`, copyName: false }
  if (kind === 'group') {
    return { kind: 'link', title: 'เปิดรายการแชทใน LINE OA · แชทกลุ่มเปิดตรงไม่ได้ คัดลอกชื่อกลุ่มไว้ให้ค้นหา', copyName: true }
  }
  const where = channel === 'line' ? 'เปิดรายการแชทใน LINE OA' : 'เปิดกล่องข้อความของเพจใน Meta Business Suite'
  return { kind: 'link', title: `${where} · คัดลอกชื่อลูกค้าไว้ให้ วางในช่องค้นหาได้เลย`, copyName: true }
}

/** The toast after the click that opened the list / inbox. */
export function copiedToast(ok: boolean, channel: Channel, kind: ConversationKind | undefined): string {
  const app = appName(channel)
  if (!ok) return `เปิด ${app} แล้ว · คัดลอกชื่อไม่สำเร็จ พิมพ์ชื่อลูกค้าค้นหาเอง`
  return kind === 'group' ? `คัดลอกชื่อกลุ่มแล้ว วางในช่องค้นหาของ ${app} ได้เลย` : `คัดลอกชื่อลูกค้าแล้ว วางในช่องค้นหาของ ${app} ได้เลย`
}

/**
 * Copy inside the click that opens the new tab. The synchronous copy goes first: right after this click the new tab
 * takes the focus, and the async Clipboard API may then refuse ("Document is not focused").
 */
export function copyInClick(text: string, doc: Document = document): Promise<boolean> {
  try {
    const t = doc.createElement('textarea')
    t.value = text
    t.setAttribute('readonly', '')
    t.style.position = 'fixed'
    t.style.left = '-9999px'
    doc.body.appendChild(t)
    t.select()
    const ok = typeof doc.execCommand === 'function' && doc.execCommand('copy')
    t.remove()
    if (ok) return Promise.resolve(true)
  } catch {
    /* fall through to the Clipboard API */
  }
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined
  if (!clip?.writeText) return Promise.resolve(false)
  return clip.writeText(text).then(
    () => true,
    () => false,
  )
}
